import React, { useEffect, useState } from 'react'
import './AdminScoresheet.css'
import { useParams } from 'react-router-dom'
import ErrorModal from '../../shared/components/UIElements/ErrorModal'
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner'

const AdminScoresheet = () => {
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState()

	const [homeRoster, setHomeRoster] = useState([])
	const [homeTeamName, setHomeTeamName] = useState('')
	const [visitorRoster, setVisitorRoster] = useState([])
	const [visitorTeamName, setVisitorTeamName] = useState('')

	const [gameStats, setGameStats] = useState([])

	const [dayOfWeek, setDayOfWeek] = useState('')
	const [date, setDate] = useState('')
	const [time, setTime] = useState('')
	const [venue, setVenue] = useState('')
	const [ref1, setRef1] = useState('')
	const [ref2, setRef2] = useState('')
	const [ref3, setRef3] = useState('')
	const [scorekeeper, setScorekeeper] = useState('')
	const [leagueName, setLeagueName] = useState('')

	const [allPenaltiesFromThisGame, setAllPenaltiesFromThisGame] = useState([])

	const gameId = useParams().gameId

	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)

			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/admin/${gameId}/rostersAndPoints`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(responseData.message)
				}

				setHomeRoster(responseData.homeRoster || [])
				setHomeTeamName(responseData.homeTeamName || '')

				setVisitorRoster(responseData.visitorRoster || [])
				setVisitorTeamName(responseData.visitorTeamName || '')

				setGameStats(responseData.gameStats || [])

				setDayOfWeek(responseData.dayOfWeek || '')
				setDate(responseData.date || '')
				setTime(responseData.time || '')
				setVenue(responseData.venue || '')

				setRef1(responseData.ref1 || '')
				setRef2(responseData.ref2 || '')
				setRef3(responseData.ref3 || '')
				setScorekeeper(responseData.scorekeeper || '')

				setLeagueName(responseData.leagueName || '')

				setAllPenaltiesFromThisGame(responseData.allPenaltiesFromThisGame || [])
			} catch (err) {
				setError(err)
			}

			setIsLoading(false)
		}

		sendRequest()
	}, [gameId])

	const errorHandler = () => {
		setError(null)
	}

	/* =========================================
	   DATE
	========================================= */

	const formatGameDate = () => {
		if (!date) {
			return ''
		}

		const splitDate = date.split('-')

		if (splitDate.length !== 3) {
			return date
		}

		const month = splitDate[0]
		const day = splitDate[1]

		const months = {
			'01': 'Jan.',
			'02': 'Feb.',
			'03': 'Mar.',
			'04': 'Apr.',
			'05': 'May',
			'06': 'June',
			'07': 'July',
			'08': 'Aug.',
			'09': 'Sept.',
			10: 'Oct.',
			11: 'Nov.',
			12: 'Dec.',
		}

		return `${months[month] || month} ${day}`
	}

	/* =========================================
	   TIME
	========================================= */

	const formatGameTime = () => {
		if (!time) {
			return ''
		}

		const [hourString, minute] = time.split(':')
		const hour = parseInt(hourString, 10)

		if (Number.isNaN(hour)) {
			return time
		}

		const suffix = hour >= 12 ? 'pm' : 'am'

		let displayHour = hour % 12

		if (displayHour === 0) {
			displayHour = 12
		}

		return `${displayHour}:${minute} ${suffix}`
	}

	/* =========================================
	   GAME SCORE
	========================================= */

	const gameStat = gameStats.length > 0 ? gameStats[0] : {}

	const homePoints1 = gameStat.homeGoalsPeriod1 || ''
	const homePoints2 = gameStat.homeGoalsPeriod2 || ''
	const homePoints3 = gameStat.homeGoalsPeriod3 || ''
	const homePoints4 = gameStat.homeGoalsPeriod4 || ''
	const homePointsTotal = gameStat.homeGoalsTotal || ''

	const visitorPoints1 = gameStat.visitorGoalsPeriod1 || ''
	const visitorPoints2 = gameStat.visitorGoalsPeriod2 || ''
	const visitorPoints3 = gameStat.visitorGoalsPeriod3 || ''
	const visitorPoints4 = gameStat.visitorGoalsPeriod4 || ''
	const visitorPointsTotal = gameStat.visitorGoalsTotal || ''

	/* =========================================
	   ROSTERS
	========================================= */

	const ROSTER_ROWS = 22

	const renderRosterRows = (roster) => {
		const rows = []

		for (let i = 0; i < ROSTER_ROWS; i++) {
			const player = roster[i]

			rows.push(
				<tr key={i}>
					<td className='scoresheet-roster-number'>
						{player ? player.number : ''}
					</td>

					<td className='scoresheet-roster-player'>
						{player
							? `${player.firstName || ''}${
									player.middleInitial ? ` ${player.middleInitial}` : ''
								} ${player.lastName || ''}`
							: ''}
					</td>
				</tr>,
			)
		}

		return rows
	}

	/* =========================================
	   BLANK SCORING ROWS
	========================================= */

	const renderScoringRows = (amount = 14) => {
		return Array.from({ length: amount }).map((_, index) => (
			<tr key={index}>
				<td>{index + 1}</td>
				<td></td>
				<td></td>
				<td></td>
				<td></td>
				<td></td>
			</tr>
		))
	}

	/* =========================================
	   PENALTIES
	========================================= */

	const homePenalties = allPenaltiesFromThisGame.filter(
		(penalty) =>
			penalty.teamName &&
			homeTeamName &&
			penalty.teamName.toLowerCase() === homeTeamName.toLowerCase(),
	)

	const visitorPenalties = allPenaltiesFromThisGame.filter(
		(penalty) =>
			penalty.teamName &&
			visitorTeamName &&
			penalty.teamName.toLowerCase() === visitorTeamName.toLowerCase(),
	)

	const renderPenaltyRows = (penalties, amount = 9) => {
		return Array.from({ length: amount }).map((_, index) => {
			const penalty = penalties[index]

			return (
				<tr key={index}>
					<td>{penalty ? penalty.period : ''}</td>

					<td>{penalty ? penalty.time : ''}</td>

					<td>{penalty ? penalty.jerseyNumber : ''}</td>

					<td>{penalty ? penalty.penaltyType : ''}</td>

					<td>{penalty ? penalty.minutes : ''}</td>
				</tr>
			)
		})
	}

	return (
		<React.Fragment>
			<ErrorModal error={error} onClear={errorHandler} />

			{isLoading && (
				<div className='center'>
					<LoadingSpinner />
				</div>
			)}

			{!isLoading && (
				<div className='adminScoresheetScreen'>
					<div className='adminScoresheetActions'>
						<button
							className='adminScoresheetPrintButton'
							onClick={() => window.print()}
						>
							PRINT SCORESHEET
						</button>
					</div>

					<div className='adminScoresheetScroll'>
						<main className='adminScoresheetPage'>
							{/* =================================
							    HEADER
							================================= */}

							<header className='scoresheet-header'>
								<h1>
									{leagueName}: {homeTeamName || 'TBD'} vs.{' '}
									{visitorTeamName || 'TBD'}
								</h1>

								<div className='scoresheet-game-details'>
									{dayOfWeek && `${dayOfWeek}, `}
									{formatGameDate()}
									{formatGameDate() && ', '}
									{formatGameTime()}
									{venue && `, ${venue}`}
								</div>

								<div className='scoresheet-officials'>
									<div className='scoresheet-official-line'>
										<span>Referees:</span>

										<div className='scoresheet-line-text'>
											{ref1}
											{ref2 && `, ${ref2}`}
											{ref3 && `, ${ref3}`}
										</div>
									</div>

									<div className='scoresheet-official-line'>
										<span>Scorekeeper:</span>

										<div className='scoresheet-line-text'>{scorekeeper}</div>
									</div>
								</div>
							</header>

							{/* =================================
							    MAIN PAGE
							================================= */}

							<div className='scoresheet-main-grid'>
								{/* ===============================
								    HOME ROSTER
								=============================== */}

								<section className='scoresheet-roster scoresheet-home-roster'>
									<h2>Home: {homeTeamName || 'TBD'}</h2>

									<table>
										<thead>
											<tr>
												<th>#</th>
												<th>Player</th>
											</tr>
										</thead>

										<tbody>{renderRosterRows(homeRoster)}</tbody>
									</table>
								</section>

								{/* ===============================
								    CENTER
								=============================== */}

								<section className='scoresheet-center'>
									{/* SCORE BY PERIOD */}

									<table className='scoresheet-period-score'>
										<thead>
											<tr>
												<th>Team</th>
												<th>Pd. 1</th>
												<th>Pd. 2</th>
												<th>Pd. 3</th>
												<th>OT</th>
												<th>Final</th>
											</tr>
										</thead>

										<tbody>
											<tr>
												<td>HOME: {homeTeamName}</td>
												<td>{homePoints1}</td>
												<td>{homePoints2}</td>
												<td>{homePoints3}</td>
												<td>{homePoints4}</td>
												<td>{homePointsTotal}</td>
											</tr>

											<tr>
												<td>AWAY: {visitorTeamName}</td>
												<td>{visitorPoints1}</td>
												<td>{visitorPoints2}</td>
												<td>{visitorPoints3}</td>
												<td>{visitorPoints4}</td>
												<td>{visitorPointsTotal}</td>
											</tr>
										</tbody>
									</table>

									{/* SCORING */}

									<div className='scoresheet-two-column-section'>
										<div className='scoresheet-box'>
											<h3>{homeTeamName || 'Home'} Scoring</h3>

											<table className='scoresheet-scoring-table'>
												<thead>
													<tr>
														<th>Goal #</th>
														<th>Per.</th>
														<th>Time</th>
														<th>Scorer</th>
														<th>A1</th>
														<th>A2</th>
													</tr>
												</thead>

												<tbody>{renderScoringRows(14)}</tbody>
											</table>
										</div>

										<div className='scoresheet-box'>
											<h3>{visitorTeamName || 'Visitor'} Scoring</h3>

											<table className='scoresheet-scoring-table'>
												<thead>
													<tr>
														<th>Goal #</th>
														<th>Per.</th>
														<th>Time</th>
														<th>Scorer</th>
														<th>A1</th>
														<th>A2</th>
													</tr>
												</thead>

												<tbody>{renderScoringRows(14)}</tbody>
											</table>
										</div>
									</div>

									{/* PENALTIES */}

									<div className='scoresheet-two-column-section scoresheet-penalty-section'>
										<div className='scoresheet-box'>
											<h3>{homeTeamName || 'Home'} Penalties</h3>

											<table className='scoresheet-penalty-table'>
												<thead>
													<tr>
														<th>Per.</th>
														<th>Time</th>
														<th>#</th>
														<th>Infraction</th>
														<th>Min.</th>
													</tr>
												</thead>

												<tbody>{renderPenaltyRows(homePenalties)}</tbody>
											</table>
										</div>

										<div className='scoresheet-box'>
											<h3>{visitorTeamName || 'Visitor'} Penalties</h3>

											<table className='scoresheet-penalty-table'>
												<thead>
													<tr>
														<th>Per.</th>
														<th>Time</th>
														<th>#</th>
														<th>Infraction</th>
														<th>Min.</th>
													</tr>
												</thead>

												<tbody>{renderPenaltyRows(visitorPenalties)}</tbody>
											</table>
										</div>
									</div>

									{/* GOALIES */}

									{/* <div className='scoresheet-two-column-section scoresheet-goalie-section'>
										<div className='scoresheet-goalie-box'>
											<h3>Home Goalie</h3>

											<table>
												<thead>
													<tr>
														<th>#</th>
														<th>Min.</th>
														<th>GA</th>
														<th>Saves</th>
													</tr>
												</thead>

												<tbody>
													<tr>
														<td></td>
														<td></td>
														<td></td>
														<td></td>
													</tr>
													<tr>
														<td></td>
														<td></td>
														<td></td>
														<td></td>
													</tr>
												</tbody>
											</table>

											<div className='scoresheet-goalie-line'>Win / Loss:</div>

											<div className='scoresheet-goalie-line'>
												Goals Against:
											</div>
										</div>

										<div className='scoresheet-goalie-box'>
											<h3>Visitor Goalie</h3>

											<table>
												<thead>
													<tr>
														<th>#</th>
														<th>Min.</th>
														<th>GA</th>
														<th>Saves</th>
													</tr>
												</thead>

												<tbody>
													<tr>
														<td></td>
														<td></td>
														<td></td>
														<td></td>
													</tr>
													<tr>
														<td></td>
														<td></td>
														<td></td>
														<td></td>
													</tr>
												</tbody>
											</table>

											<div className='scoresheet-goalie-line'>Win / Loss:</div>

											<div className='scoresheet-goalie-line'>
												Goals Against:
											</div>
										</div>
									</div> */}
								</section>

								{/* ===============================
								    VISITOR ROSTER
								=============================== */}

								<section className='scoresheet-roster scoresheet-visitor-roster'>
									<h2>Visitor: {visitorTeamName || 'TBD'}</h2>

									<table>
										<thead>
											<tr>
												<th>#</th>
												<th>Player</th>
											</tr>
										</thead>

										<tbody>{renderRosterRows(visitorRoster)}</tbody>
									</table>
								</section>
							</div>

							{/* =================================
							    NOTES
							================================= */}

							{/* <section className='scoresheet-notes'>
								<div className='scoresheet-notes-label'>Game Notes:</div>

								<div className='scoresheet-note-line'></div>
								<div className='scoresheet-note-line'></div>
								<div className='scoresheet-note-line'></div>
							</section> */}

							{/* =================================
							    SIGNATURES
							================================= */}

							{/* <section className='scoresheet-signatures'>
								<div className='scoresheet-signature'>
									<span>Referee 1:</span>
									<div></div>
								</div>

								<div className='scoresheet-signature'>
									<span>Referee 2:</span>
									<div></div>
								</div>

								<div className='scoresheet-signature'>
									<span>Scorekeeper:</span>
									<div></div>
								</div>
							</section> */}
						</main>
					</div>
				</div>
			)}
		</React.Fragment>
	)
}

export default AdminScoresheet
